import cn from 'classnames';
import { ReactNode } from 'react';
import { CloseIcon } from '@/assets/icons/CloseIcon';
import { type ButtonProps, Button } from '@/ui-components/atoms/Button/Button';
import { Text } from '@/ui-components/atoms/Text/Text';
import styles from './announcementbanner.module.scss';

export interface AnnouncementBannerProps {
    ariaLabel?: string;
    ariaLive?: 'off' | 'polite' | 'assertive';
    colorSet?: 'base' | 'inverse' | 'sxmBlue';
    leadingIcon?: ReactNode;
    role?: 'region' | 'status';
    text: ReactNode;
    ctas?: ButtonProps[];
    onDismissButtonClick?: () => void; // Button hides when not provided
    layout?: 'default' | 'geo';
}

export function AnnouncementBanner({
    ariaLabel,
    ariaLive,
    colorSet = 'base',
    leadingIcon,
    role,
    text,
    ctas,
    onDismissButtonClick,
    layout = 'default',
}: AnnouncementBannerProps): React.JSX.Element {
    const isGeoLayout = layout === 'geo';
    const textTag = isGeoLayout ? 'div' : 'span';
    const wrapperStyles = cn(styles[`colorSet-${colorSet}`], {
        [styles.geoLayout]: isGeoLayout,
    });
    const contentWrapperStyles = cn(styles.contentWrapper, {
        [styles.noDismissbutton]: !onDismissButtonClick,
        [styles.geoContentWrapper]: isGeoLayout,
    });
    const ctasColorSet = colorSet === 'sxmBlue' ? 'subset-sxm-blue' : colorSet;

    return (
        <div className={wrapperStyles} aria-label={ariaLabel} aria-live={ariaLive} role={role}>
            <div className={contentWrapperStyles}>
                {leadingIcon && (
                    <div
                        className={cn(styles.leadingIconWrapper, styles.iconWrapper, {
                            [styles.geoLeadingIconWrapper]: isGeoLayout,
                        })}
                    >
                        {leadingIcon}
                    </div>
                )}

                <div className={styles.textWrapper}>
                    <Text tag={textTag} variant="text-medium" className={styles.text}>
                        {text}
                    </Text>

                    {!!ctas?.length && (
                        <div
                            className={cn(styles.ctasWrapper, {
                                [styles.geoCtasWrapper]: isGeoLayout,
                            })}
                        >
                            {ctas.map((cta, index) => (
                                <Button
                                    key={index}
                                    {...cta}
                                    type="button"
                                    fitting="hug-contents"
                                    variant="tertiary"
                                    colorSet={ctasColorSet}
                                    tertiaryNoPadding={true}
                                    size="small"
                                    className={cn(styles.cta, {
                                        [styles.geoCta]: isGeoLayout,
                                    })}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {onDismissButtonClick && (
                    <Button
                        type="button"
                        onClick={onDismissButtonClick}
                        iconButton={true}
                        fitting="hug-contents"
                        variant="tertiary"
                        tertiaryNoPadding={true}
                        size="large"
                        ariaLabel="Dismiss announcement"
                        className={cn(styles.dismissButton, styles.iconWrapper, {
                            [styles.geoDismissButton]: isGeoLayout,
                        })}
                    >
                        <CloseIcon className={styles.dismissIcon} />
                    </Button>
                )}
            </div>
        </div>
    );
}
